import { interpolatingMedian, weightedMedian } from "../../utils/median.js";
import { t as geodesicAngleRad } from "../../geodesic-angle-CMHpPv_b.js";
import { r as averageRotation } from "../../qr-pose-aggregation-Ce3agYE2.js";
import { mintQrLevelFromWorld, qrWorldPoseFromOdom } from "./qr-mint-level.js";
//#region ../src/ar/qr/qr-anchor-mint.ts
/**
* Minting one printed code's world anchor from a whole recording's sightings.
*
* This is the step that turns "the camera saw this code eight times over three
* minutes" into a single geo pose a later visitor can relocalize against.
*
* THREE DECISIONS LIVE HERE, and each was argued before it was coded — see
* `GpsPlusSlamJs_Docs/docs/2026-08-28-0636-recorder-qr-anchor-authoring-plan.md`
* §3 M-C:
*
* 1. **Did the code move?** (DEC-4) Measured as the rotation disagreement
*    ACROSS sightings, in the ODOMETRY frame — GPS never enters, so what is
*    measured is SLAM drift plus real movement rather than alignment churn.
*    Translation disagreement is reported but does not gate: over a
*    three-minute walk, drift and a genuinely moved poster produce the same
*    magnitude, so that threshold cannot be set honestly before field data.
*
* 2. **Which alignment?** ONE for every sighting: the one the caller passes
*    as `currentAlignment` when it is in their odometry segment, else the
*    newest sighting's own snapshot. The caller decides which (owner
*    decision D28, revised 2026-10-02, `qr-mint-alignment-tracker.ts`): the
*    FIRST MATURE alignment (40 m of session GPS extent, D34) at or after the
*    code's last sighting; before maturity the alignment at save, or the one
*    the code's segment closed with. This superseded DEC-3 (each sighting
*    through the alignment as it stood AT that sighting): a sighting taken
*    before the alignment had any walk behind it carries an arbitrary yaw,
*    and through it a recording that started at the code minted a heading
*    72 degrees off p50 and a position 2.9 m off p50, against 1.0-4.9
*    degrees from 30 m walks up (7-10 at 15 m) and 1.3 m through a matured
*    alignment (`qr-anchor-mint.start-at-code.test.ts`). It also superseded
*    the alignment at save for every code (a2, shipped for one day), under
*    which a code seen mid-recording and then walked away from inherited all
*    SLAM drift after its sighting: 8.6 m p50 at 500 m with 1 % and 1 degree
*    per 100 m, against 1.1-1.7 m through the first mature alignment (the
*    same file, `left`).
*
* 3. **How are they combined?** The position is a recency-weighted median,
*    the rotation a robust unweighted average. The weighting is what is left
*    of DEC-3, and its original reason (a later sighting carried a later,
*    better alignment) went with the per-sighting alignment: with one
*    alignment it only prefers the later viewpoints, which also carry more
*    drift. On the start-at-code sweep it changes nothing measurable (a 1e9 s
*    half-life gives the same p50 to 0.1 m and 0.1 degrees), so it stays,
*    unearned, until the field probe; both the weighted and the unweighted
*    answer are returned, so the difference is visible.
*/
/**
* Rotation disagreement across sightings above which the code is declared
* MOVED and no anchor is written.
*
* A GUESS until the field recordings measure it. The planned negative-case
* recording re-hangs the poster rotated by twenty degrees or more, so the
* threshold sits below that and above what SLAM drift plausibly contributes
* over a three-minute walk. The probe replaces it with a measured number.
*/
const DEFAULT_MAX_FIXED_ROTATION_SPREAD_DEG = 15;
/**
* How fast a sighting's influence decays with age (seconds).
*
* Weight is `1 / (1 + age / halfLife)`, age measured back from the LAST
* sighting. Also a guess — the probe sets it, and until then the summary
* screen shows the unweighted answer alongside so the difference is visible
* in the field rather than assumed.
*/
const DEFAULT_RECENCY_HALF_LIFE_S = 60;
/**
* GPS extent (m) of the alignment a code is composed through below which
* the level is saved but marked `headingUncertain` (owner decision D31).
*
* Measured (`qr-anchor-mint.start-at-code.test.ts`, `extent` sweep: a
* recording that starts at the code, 3 looks, out-and-back walks of 0-30 m,
* 40 seeds each, 5 m GPS accuracy, the shipped mint): heading 41 degrees
* p50 at 0-5 m of extent, 7.3 at 5-10 m, 3.5 at 10-15 m. Swept as a marker:
* - 5 m marks 36 % of those codes and leaves the 5-10 m bin (7.3 / 22
*   degrees p50 / p90) unmarked: the unmarked codes are 4.9 / 16.
* - 10 m marks 74 %: marked 13.8 / 88 degrees, unmarked 3.4 / 8.
* - 15 m marks 89 % and buys nothing: the unmarked are still 3.4 / 8, so
*   it marks codes whose heading is as good as the rest.
* The shares describe that deliberately short-walk set, not real
* recordings. At 2-3 m GPS accuracy standing still spans less, so the same
* extent would mean a longer real walk and a lower value could serve.
*/
const QR_MINT_HEADING_UNCERTAIN_EXTENT_M = 10;
/**
* The cross-sighting rotation disagreement, OUTLIER-INCLUSIVE.
*
* Deliberately NOT `aggregateQrPose`/`averageRotation`: that function's
* spread is documented as the max angle among the INLIERS to its robust mean,
* with a 12-degree inlier threshold. A poster re-hung at twenty degrees is
* therefore discarded as an outlier and the reported spread stays SMALL —
* which would make this gate blind to exactly the case it exists to catch.
* (M-A/M-C cold review, blocker 3.)
*/
function maxPairwiseRotationDeg(rotations) {
	let worst = 0;
	for (let i = 0; i < rotations.length; i += 1) for (let j = i + 1; j < rotations.length; j += 1) {
		const a = rotations[i];
		const b = rotations[j];
		if (a === void 0 || b === void 0) continue;
		const deg = geodesicAngleRad(a, b) * 180 / Math.PI;
		if (deg > worst) worst = deg;
	}
	return worst;
}
/** Max pairwise distance between positions (m) — reported, never gating. */
function maxPairwiseDistanceM(positions) {
	let worst = 0;
	for (let i = 0; i < positions.length; i += 1) for (let j = i + 1; j < positions.length; j += 1) {
		const a = positions[i];
		const b = positions[j];
		if (a === void 0 || b === void 0) continue;
		const d = Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
		if (d > worst) worst = d;
	}
	return worst;
}
/**
* Everything that can refuse before any composition happens: no evidence, a
* frame change, or a code that turned too far between visits.
*/
function refuseUnusable(input, rotationSpreadDeg) {
	const maxSpread = input.maxFixedRotationSpreadDeg ?? 15;
	if (input.sightings.length === 0) return {
		ok: false,
		reason: "no-sightings",
		detail: "This code was never seen for long enough to place it."
	};
	if (input.spansFrameChange) return {
		ok: false,
		reason: "frame-changed",
		detail: "Tracking restarted while this code was being recorded, so its sightings are in different frames and cannot be compared."
	};
	if (rotationSpreadDeg > maxSpread) return {
		ok: false,
		reason: "moved",
		detail: `This code turned by ${rotationSpreadDeg.toFixed(1)}° between sightings, so it was probably moved. Nothing was written for it.`
	};
	return null;
}
/**
* The caller's half-life, or the default — rejecting values that would make
* {@link recencyWeights} produce weights `weightedMedian` silently drops
* (PR #390 review).
*
* `0` yields NaN for the newest sighting and 0 for every older one; a negative
* value can make the denominator exactly 0, yielding Infinity. In both cases
* `weightedMedian` discards the lot and falls back to the unweighted median,
* so the weighting does not run AND `quality.unweighted` matches the weighted
* answer — the readout that exists to show the weighting's effect reports
* "0 m moved" precisely when it never happened. Loud beats silent here.
*
* "No decay" is expressible as a large finite half-life; Infinity is rejected
* so the contract stays a single positive finite number.
*/
function resolveRecencyHalfLifeS(value) {
	const halfLifeS = value ?? 60;
	if (!Number.isFinite(halfLifeS) || halfLifeS <= 0) throw new RangeError("recencyHalfLifeS must be a positive, finite number of seconds; got " + String(halfLifeS));
	return halfLifeS;
}
/** `1 / (1 + age / halfLife)`, age measured back from the last sighting. */
function recencyWeights(sightings, halfLifeS) {
	const lastAt = sightings.at(-1)?.lastTimestamp ?? 0;
	return sightings.map((s) => {
		return 1 / (1 + Math.max(0, (lastAt - s.lastTimestamp) / 1e3) / halfLifeS);
	});
}
/**
* The ONE alignment every sighting is placed through: the most informed one
* that still describes the sightings' odometry frame, or `null` when there
* is none at all.
*
* Not each sighting's own (DEC-3, superseded by the owner on 2026-10-02): an
* alignment's yaw is unobservable until the walk has a baseline, so a
* sighting taken as the recording starts carries an arbitrary yaw. Measured
* on the start-at-code sweep (40 recordings per cell, walks 15-120 m, 1-3
* looks, yaw noise 1-5 degrees): per-sighting composition gave 72 degrees
* heading p50 and, for a code seen only at the start, 2.9 m; through the
* alignment at save 1.0-4.9 degrees from 30 m walks up (7-10 at 15 m) and
* 1.3 m. The cost: with three looks the old rotation average was up to 0.6
* degrees better at p50 (2.7 at 15 m), while its p90 reached 146. WHICH
* alignment the caller passes is decided outside (header, decision 2).
*
* The caller's alignment is used only when it is in the sightings' segment:
* after a tracking restart or loop closure it describes another frame, and
* the newest snapshot taken in their own frame is then the best available.
*/
function mintFrame(sightings, current) {
	if (current?.alignmentMatrix != null && current.zero !== null && sightings.every((s) => s.segment === current.segment)) return {
		alignmentMatrix: current.alignmentMatrix,
		zero: current.zero,
		info: alignmentInfo(current.alignmentSampleCount, current.gpsAccuracyM),
		...usableExtent(current.gpsExtentM) ? { gpsExtentM: current.gpsExtentM } : {}
	};
	for (let i = sightings.length - 1; i >= 0; i -= 1) {
		const s = sightings[i];
		if (s?.alignmentMatrix == null || s.zero === null) continue;
		return {
			alignmentMatrix: s.alignmentMatrix,
			zero: s.zero,
			info: alignmentInfo(s.alignmentSampleCount, s.gpsAccuracyM)
		};
	}
	return null;
}
/** A GPS extent that says something about the walk: finite, not negative. */
function usableExtent(m) {
	return m !== void 0 && Number.isFinite(m) && m >= 0;
}
/**
* The D31 marker for a level composed through an alignment whose session
* GPS extent is `gpsExtentM`: the extent and whether it is under
* {@link QR_MINT_HEADING_UNCERTAIN_EXTENT_M}, for the level's
* `mintQuality`. Empty when the extent is unknown, non-finite or negative,
* so the level says nothing rather than a default (absent = unknown).
*
* Exported as THE marker rule (DEC-H3): the Tour Viewer's authoring settle
* re-mints a code through an alignment of its own choosing and marks it
* with this (review R7 of D33).
*/
function qrMintHeadingMarker(gpsExtentM) {
	if (!usableExtent(gpsExtentM)) return {};
	return {
		alignmentGpsExtentM: gpsExtentM,
		headingUncertain: gpsExtentM < 10
	};
}
/** The alignment facts the level assembly needs. */
function alignmentInfo(sampleCount, gpsAccuracyM) {
	return {
		hasMatrix: true,
		sampleCount,
		...gpsAccuracyM !== void 0 ? { gpsAccuracyM } : {}
	};
}
/** The combined world position, weighted and unweighted, plus the robust
*  rotation — `null` when no orientation could be formed at all. */
function combinePlacements(worlds, weights) {
	const xs = worlds.map((w) => w.position.x);
	const ys = worlds.map((w) => w.position.y);
	const zs = worlds.map((w) => w.position.z);
	const averaged = averageRotation(worlds.map((w) => w.rotation));
	const flat = worlds.map(() => 1);
	return {
		weighted: {
			x: weightedMedian(xs, weights),
			y: weightedMedian(ys, weights),
			z: weightedMedian(zs, weights)
		},
		unweighted: {
			x: weightedMedian(xs, flat),
			y: weightedMedian(ys, flat),
			z: weightedMedian(zs, flat)
		},
		rotation: averaged?.quat ?? worlds.at(-1)?.rotation ?? null
	};
}
/** The quality block, before the unweighted comparison is filled in. */
function buildQuality(sightings, sortedSizes, rotationSpreadDeg, translationSpreadM) {
	return {
		sightingCount: sightings.length,
		detectionCount: sightings.reduce((sum, s) => sum + s.detectionCount, 0),
		rotationSpreadDeg,
		translationSpreadM,
		sizeM: interpolatingMedian(sortedSizes),
		sizeSpreadM: sortedSizes.length === 0 ? 0 : (sortedSizes.at(-1) ?? 0) - (sortedSizes[0] ?? 0),
		sightingsSeen: sightings.length
	};
}
/**
* Place every sighting through the one alignment and combine them - or the
* reason that was not possible. Both refusals live here so the entry point stays one
* straight line, which is also what keeps it under the complexity budget.
*/
function placeOrRefuse(sightings, halfLifeS, current) {
	const frame = mintFrame(sightings, current);
	if (frame === null) return { refusal: {
		ok: false,
		reason: "no-alignment",
		detail: "This code was seen, but the session never had a GPS alignment to place it against."
	} };
	const combined = combinePlacements(sightings.map((s) => qrWorldPoseFromOdom(s.odomPose, frame.alignmentMatrix)), recencyWeights(sightings, halfLifeS));
	if (combined.rotation === null) return { refusal: {
		ok: false,
		reason: "no-alignment",
		detail: "No usable orientation could be combined for this code."
	} };
	return {
		frame,
		combined,
		rotation: combined.rotation
	};
}
/**
* Combine one code's sightings into an anchor, or decline with a reason.
*
* (This doc comment sat above `refuseUnusable` until 2026-08-31, orphaned by a
* refactor — it has always described THIS function.)
*
* **Never throws for a DATA condition.** The callers are a zip contributor and
* a summary panel, and both want a verdict rather than an exception, so "never
* seen", "tracking restarted" and "the poster moved" all come back as
* `{ ok: false, reason }`.
*
* **It does throw `RangeError` for a caller BUG** — today only a
* `recencyHalfLifeS` that is not a positive finite number. That is not a
* softening of the contract above but its complement: a bad half-life is not
* something the recording did, it is something the calling code did, and
* {@link resolveRecencyHalfLifeS} records why degrading silently is the worse
* option (the readout that would reveal it is the one it suppresses).
*/
function mintQrAnchorFromSightings(input) {
	const { sightings, nowIso } = input;
	const recencyHalfLifeS = resolveRecencyHalfLifeS(input.recencyHalfLifeS);
	const rotationSpreadDeg = maxPairwiseRotationDeg(sightings.map((s) => s.odomPose.rotation));
	const translationSpreadM = maxPairwiseDistanceM(sightings.map((s) => s.odomPose.position));
	const refusal = refuseUnusable(input, rotationSpreadDeg);
	if (refusal !== null) return refusal;
	const placed = placeOrRefuse(sightings, recencyHalfLifeS, input.currentAlignment);
	if ("refusal" in placed) return placed.refusal;
	const { frame, combined, rotation } = placed;
	const quality = buildQuality(sightings, sightings.map((s) => s.sizeM).sort((a, b) => a - b), rotationSpreadDeg, translationSpreadM);
	const shared = {
		zero: frame.zero,
		alignment: frame.info,
		sizeM: quality.sizeM,
		nowIso
	};
	const level = mintQrLevelFromWorld({
		...shared,
		world: {
			position: combined.weighted,
			rotation
		},
		quality: {
			sightingCount: quality.sightingCount,
			detectionCount: quality.detectionCount,
			rotationSpreadDeg: quality.rotationSpreadDeg,
			translationSpreadM: quality.translationSpreadM,
			physicalSizeSpreadM: quality.sizeSpreadM,
			...qrMintHeadingMarker(frame.gpsExtentM)
		}
	});
	if (!level.ok) return {
		ok: false,
		reason: "no-alignment",
		detail: level.error
	};
	const plain = mintQrLevelFromWorld({
		...shared,
		world: {
			position: combined.unweighted,
			rotation
		}
	});
	const plainGeo = plain.ok ? plain.level.qr.geo : void 0;
	if (plainGeo !== void 0) quality.unweighted = {
		lat: plainGeo.lat,
		lon: plainGeo.lon,
		alt: plainGeo.alt
	};
	return {
		ok: true,
		level,
		quality
	};
}
//#endregion
export { DEFAULT_MAX_FIXED_ROTATION_SPREAD_DEG, DEFAULT_RECENCY_HALF_LIFE_S, QR_MINT_HEADING_UNCERTAIN_EXTENT_M, maxPairwiseRotationDeg, mintQrAnchorFromSightings, qrMintHeadingMarker };
